import { useState, useMemo, useEffect, useRef } from "react";
import { TrendingUp, Clock, Briefcase, Calendar, Target, Percent } from "lucide-react";
import SectionHeading, { SectionDivider } from "@/components/SectionHeading";
import { formatMoney, CURRENCIES } from "@/lib/currency";
import { normalizeDigits, localizeDigits } from "@/lib/digits";
import CurrencySwitcher from "@/components/CurrencySwitcher";

const T = {
  ar: {
    eyebrow: "ميزة جديدة",
    title: "حاسبة الربح الشهري المتوقع",
    subtitle:
      "اعرف دخلك الشهري والسنوي بناءً على ساعات عملك أو عدد مشاريعك — مع احتساب العمولة والضريبة.",
    modeHourly: "بالساعة",
    modeProjects: "بالمشروع",
    rateLabel: "سعر الساعة (ريال)",
    hoursLabel: "ساعات العمل الأسبوعية",
    projectRateLabel: "متوسط سعر المشروع (ريال)",
    projectsLabel: "عدد المشاريع شهريًا",
    commissionLabel: "عمولة المنصة",
    taxLabel: "ضريبة القيمة المضافة",
    taxNote: "تطبق إذا تجاوز الدخل السنوي 375,000 ريال",
    enableTax: "فعّل الضريبة",
    disableTax: "بدون ضريبة",
    weeklyIncome: "الدخل الأسبوعي",
    monthlyGross: "الدخل الشهري الإجمالي",
    monthlyNet: "الدخل الشهري الصافي",
    yearlyProjection: "التوقع السنوي",
    scenarios: "سيناريوهات النمو",
    scenarioNow: "الوضع الحالي",
    scenarioMid: "متوسط النمو",
    scenarioPro: "محترف متفرّغ",
    scenarioNowDesc: "إعداداتك الحالية",
    scenarioMidDesc: "+30% ساعات/مشاريع",
    scenarioProDesc: "+80% ساعات/مشاريع",
    sar: "ريال",
    per: "/شهر",
    perYear: "/سنة",
    dir: "rtl",
  },
  en: {
    eyebrow: "New feature",
    title: "Expected Monthly Earnings Calculator",
    subtitle:
      "See your monthly and yearly income based on your hours or project count — with commission and tax included.",
    modeHourly: "Hourly",
    modeProjects: "Per project",
    rateLabel: "Hourly rate (SAR)",
    hoursLabel: "Weekly work hours",
    projectRateLabel: "Avg project price (SAR)",
    projectsLabel: "Projects per month",
    commissionLabel: "Platform commission",
    taxLabel: "VAT",
    taxNote: "Applies when annual income exceeds 375,000 SAR",
    enableTax: "Apply VAT",
    disableTax: "No VAT",
    weeklyIncome: "Weekly income",
    monthlyGross: "Monthly gross",
    monthlyNet: "Monthly net",
    yearlyProjection: "Yearly projection",
    scenarios: "Growth scenarios",
    scenarioNow: "Current",
    scenarioMid: "Growth",
    scenarioPro: "Full-time pro",
    scenarioNowDesc: "Your current setup",
    scenarioMidDesc: "+30% hours/projects",
    scenarioProDesc: "+80% hours/projects",
    sar: "SAR",
    per: "/mo",
    perYear: "/yr",
    dir: "ltr",
  },
};

function fmt(n, digitsMode) {
  // Always format with English thousands separators to match the Casio LCD look,
  // then optionally swap digits to Arabic-Indic based on the user's preference.
  const formatted = (Math.round(n) || 0).toLocaleString("en-US", { useGrouping: true });
  return digitsMode === "ar" ? localizeDigits(formatted, "ar") : formatted;
}

export default function EarningsCalculator({ lang = "ar", theme = "dark", currency = "SAR", setCurrency, digits = "ar" }) {
  const L = T[lang] || T.ar;
  const dark = theme === "dark";
  const C = CURRENCIES[currency] || CURRENCIES.SAR;
  const symbol = lang === "ar" ? C.symbol_ar : C.symbol_en;
  // Replace the hard-coded "ريال" / "SAR" copy with the active currency symbol.
  const unit = symbol;
  const rateLabel = (lang === "ar" ? `سعر الساعة (${symbol})` : `Hourly rate (${symbol})`);
  const projectRateLabel = (lang === "ar" ? `متوسط سعر المشروع (${symbol})` : `Avg project price (${symbol})`);
  const taxNote = (lang === "ar"
    ? `تطبق إذا تجاوز الدخل السنوي 375,000 ${symbol}`
    : `Applies when annual income exceeds 375,000 ${symbol}`);
  const [mode, setMode] = useState("hourly"); // "hourly" or "projects"
  const [hourlyRate, setHourlyRate] = useState(150);
  const [weeklyHours, setWeeklyHours] = useState(25);
  const [projectRate, setProjectRate] = useState(2500);
  const [projectsPerMonth, setProjectsPerMonth] = useState(4);
  const [commission, setCommission] = useState(10);
  const [applyTax, setApplyTax] = useState(false);

  // Ref to the LCD readout + helper that scrolls it into view. Only called
  // when the explicit "احسب دخلك" button is pressed — NOT on every input
  // change, per user request.
  const lcdRef = useRef(null);
  const scrollToLCD = () => {
    lcdRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // When the display currency changes, rescale the amount inputs so the
  // calculator reflects the new currency (not just a relabel).
  const prevCurrency = useRef(currency);
  useEffect(() => {
    const prev = prevCurrency.current;
    if (prev === currency) return;
    const oldRate = (CURRENCIES[prev] || CURRENCIES.SAR).rate;
    const newRate = (CURRENCIES[currency] || CURRENCIES.SAR).rate;
    const factor = newRate / oldRate;
    const decimals = ["KWD", "BHD"].includes(currency) ? 2 : 0;
    const round = (v) => {
      const p = Math.pow(10, decimals);
      return Math.round(v * factor * p) / p;
    };
    setHourlyRate((v) => round(v));
    setProjectRate((v) => round(v));
    prevCurrency.current = currency;
  }, [currency]);

  const compute = (hours, projects) => {
    const gross =
      mode === "hourly" ? hourlyRate * hours * 4.33 : projectRate * projects;
    const afterCommission = gross * (1 - commission / 100);
    const yearlyGross = gross * 12;
    const effectiveTax = applyTax ? 15 : 0;
    const net = afterCommission * (1 - effectiveTax / 100);
    return {
      gross,
      net,
      yearly: net * 12,
      weekly: gross / 4.33,
      yearlyGross,
    };
  };

  const now = useMemo(() => compute(weeklyHours, projectsPerMonth), [
    hourlyRate, weeklyHours, projectRate, projectsPerMonth, mode, commission, applyTax,
  ]);
  const mid = useMemo(() => compute(weeklyHours * 1.3, projectsPerMonth * 1.3), [
    hourlyRate, weeklyHours, projectRate, projectsPerMonth, mode, commission, applyTax,
  ]);
  const pro = useMemo(() => compute(weeklyHours * 1.8, projectsPerMonth * 1.8), [
    hourlyRate, weeklyHours, projectRate, projectsPerMonth, mode, commission, applyTax,
  ]);

  return (
    <section
      id="earnings"
      data-testid="earnings-section"
      dir={L.dir}
      className={`relative ${dark ? "bg-[#182138]" : "bg-white"}`}
    >
      <SectionDivider theme={theme} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
        {/* Tiny inline title row — replaces the large hero heading to save vertical space. */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-extrabold ${dark ? "text-[#18c98f]" : "text-[#12a777]"}`}>
            <TrendingUp className="h-3.5 w-3.5" />
            {L.title}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {/* Results — LCD-style display at the TOP (calculator face) */}
          <div className="space-y-4">
            {/* The LCD screen — authentic Casio-style light olive LCD with crisp dark readout + green frame */}
            <div
              ref={lcdRef}
              data-testid="earnings-display"
              className="relative"
              style={{
                padding: "3px",
                borderRadius: "18px",
                border: "2px solid #18c98f",
                background: "linear-gradient(180deg, #b5b89d 0%, #c8cbb0 50%, #a8ab90 100%)",
                boxShadow: dark
                  ? "0 0 24px rgba(24,201,143,0.45), 0 24px 60px -24px rgba(0,0,0,0.5), 0 4px 0 0 rgba(0,0,0,0.3) inset"
                  : "0 0 20px rgba(24,201,143,0.35), 0 24px 60px -24px rgba(0,0,0,0.25), 0 4px 0 0 rgba(0,0,0,0.08) inset",
              }}
            >
              <div
                className="relative overflow-hidden"
                style={{
                  borderRadius: "15px",
                  background: "linear-gradient(180deg, #c2c5aa 0%, #b2b59a 100%)",
                  boxShadow: "inset 0 0 60px rgba(0,0,0,0.12), inset 0 2px 0 rgba(0,0,0,0.08)",
                }}
              >
                {/* LCD horizontal lines texture */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-[0.1] pointer-events-none"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(0deg, transparent 0, transparent 3px, currentColor 3px, currentColor 4px)",
                    color: "#1a1a0d",
                  }}
                />
                <div className="relative px-4 py-4 sm:px-6 sm:py-5 text-center">
                  <div
                    className="text-[10px] uppercase tracking-[0.4em] font-black"
                    style={{ color: "#4a2b00" }}
                  >
                    {L.monthlyNet}
                  </div>
                  <div
                    className="mt-1 lcd-digits font-black text-5xl sm:text-6xl leading-none"
                    dir="ltr"
                    style={{
                      color: "#7c2d12",
                      textShadow: "0 1px 1px rgba(0,0,0,0.25), 0 0 1px rgba(124,45,18,0.8)",
                      WebkitTextStroke: "0.5px #7c2d12",
                    }}
                  >
                    {fmt(now.net, digits)}
                  </div>
                  <div
                    className="mt-1 text-[12px] font-black"
                    dir="ltr"
                    style={{ color: "#4a2b00" }}
                  >
                    {unit}{L.per}
                  </div>
                </div>
              </div>
            </div>

            {/* Compact mini numbers strip — "sub-display" */}
            <div
              data-testid="earnings-mini-strip"
              className={`grid grid-cols-3 rounded-2xl border divide-x ${dark ? "bg-[#1f2a47] border-[#2e3a5c] divide-[#2e3a5c]" : "bg-white border-[#e5e5ea] divide-[#e5e5ea]"} ${lang === "ar" ? "divide-x-reverse" : ""}`}
            >
              <MiniStat label={L.weeklyIncome} value={fmt(now.weekly, digits)} unit={unit} dark={dark} />
              <MiniStat label={L.monthlyGross} value={fmt(now.gross, digits)} unit={unit} dark={dark} />
              <MiniStat label={L.yearlyProjection} value={fmt(now.yearly, digits)} unit={unit} dark={dark} />
            </div>

            {/* Scenarios — compact 3-column strip (same layout as the yearly projection row above) */}
            <div className="space-y-2">
              <div className={`text-[10px] uppercase tracking-[0.3em] font-bold px-1 ${dark ? "text-[#18c98f]/80" : "text-[#12a777]"}`}>
                {L.scenarios}
              </div>
              <div
                data-testid="earnings-scenarios-strip"
                className={`grid grid-cols-3 rounded-2xl border divide-x ${dark ? "bg-[#1f2a47] border-[#2e3a5c] divide-[#2e3a5c]" : "bg-white border-[#e5e5ea] divide-[#e5e5ea]"} ${lang === "ar" ? "divide-x-reverse" : ""}`}
              >
                <MiniStat label={L.scenarioNow} value={fmt(now.net, digits)} unit={unit + L.per} dark={dark} />
                <MiniStat label={L.scenarioMid} value={fmt(mid.net, digits)} unit={unit + L.per} dark={dark} />
                <MiniStat label={L.scenarioPro} value={fmt(pro.net, digits)} unit={unit + L.per} dark={dark} />
              </div>
            </div>
          </div>

          {/* Inputs — the "calculator buttons" below the display */}
          <div
            className={`rounded-2xl p-3 sm:p-4 border ${dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]"}`}
          >
            {/* Mode toggle + inline currency selector — single row, no wrap */}
            <div className="flex items-center justify-between gap-2">
              <div className={`inline-flex p-0.5 rounded-full ${dark ? "bg-[#182138]" : "bg-white"} border ${dark ? "border-[#2e3a5c]" : "border-[#e5e5ea]"}`}>
                <button
                  data-testid="earnings-mode-hourly"
                  onClick={() => setMode("hourly")}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-bold transition-all inline-flex items-center gap-1 ${
                    mode === "hourly"
                      ? "bg-[#18c98f] text-white"
                      : dark ? "text-white/60 hover:text-white" : "text-[#2d2c35]/60"
                  }`}
                >
                  <Clock className="h-3.5 w-3.5" />
                  {L.modeHourly}
                </button>
                <button
                  data-testid="earnings-mode-projects"
                  onClick={() => setMode("projects")}
                  className={`px-2.5 py-1.5 rounded-full text-xs font-bold transition-all inline-flex items-center gap-1 ${
                    mode === "projects"
                      ? "bg-[#18c98f] text-white"
                      : dark ? "text-white/60 hover:text-white" : "text-[#2d2c35]/60"
                  }`}
                >
                  <Briefcase className="h-3.5 w-3.5" />
                  {L.modeProjects}
                </button>
              </div>
              {setCurrency && (
                <CurrencySwitcher
                  lang={lang}
                  theme={theme}
                  currency={currency}
                  setCurrency={setCurrency}
                  testIdPrefix="earnings-currency"
                  size="sm"
                />
              )}
            </div>

            {/* 2x2 compact grid of inputs — wrapped in editable-hint ring so
                the user clearly sees which fields drive the LCD readout. */}
            <div className="mt-3 p-editable-hint p-3">
              <div className="mb-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold text-[#f97316] font-arabic">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#f97316] animate-pulse" />
                {lang === "ar" ? "عدّل الحقول لتتغير النتيجة" : "Edit fields to update the result"}
              </div>
              <div className="grid grid-cols-2 gap-2">
              {mode === "hourly" ? (
                <>
                  <Field
                    label={rateLabel}
                    value={hourlyRate}
                    onChange={(v) => setHourlyRate(Math.max(0, Number(v)))}
                    
                    testId="earnings-rate"
                    dark={dark}
                    lang={lang}
                    digits={digits}
                    compact
                  />
                  <Field
                    label={L.hoursLabel}
                    value={weeklyHours}
                    onChange={(v) => setWeeklyHours(Math.max(0, Math.min(80, Number(v))))}
                    
                    testId="earnings-hours"
                    dark={dark}
                    lang={lang}
                    digits={digits}
                    max={80}
                    compact
                  />
                </>
              ) : (
                <>
                  <Field
                    label={projectRateLabel}
                    value={projectRate}
                    onChange={(v) => setProjectRate(Math.max(0, Number(v)))}
                    
                    testId="earnings-project-rate"
                    dark={dark}
                    lang={lang}
                    digits={digits}
                    compact
                  />
                  <Field
                    label={L.projectsLabel}
                    value={projectsPerMonth}
                    onChange={(v) => setProjectsPerMonth(Math.max(0, Math.min(30, Number(v))))}
                    
                    testId="earnings-projects"
                    dark={dark}
                    lang={lang}
                    digits={digits}
                    max={30}
                    compact
                  />
                </>
              )}

              <Field
                label={L.commissionLabel + " (%)"}
                value={commission}
                onChange={(v) => setCommission(Math.max(0, Math.min(50, Number(v))))}
                
                testId="earnings-commission"
                dark={dark}
                lang={lang}
                digits={digits}
                max={50}
                icon={<Percent className="h-3 w-3 opacity-50" />}
                compact
              />

              {/* Tax toggle — full tap-target button with clear ON/OFF state */}
              <button
                data-testid="earnings-tax-toggle"
                onClick={() => setApplyTax(!applyTax)}
                className={`group flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 border transition-all text-start w-full ${
                  applyTax
                    ? "bg-[#18c98f]/15 border-[#18c98f] shadow-[0_0_0_1px_rgba(24,201,143,0.4)]"
                    : dark
                      ? "bg-[#182138] border-[#2e3a5c] hover:border-[#f97316]/60"
                      : "bg-white border-[#e5e5ea] hover:border-[#f97316]/60"
                }`}
              >
                <div className="min-w-0 flex items-center gap-1.5">
                  <Percent className={`h-3.5 w-3.5 shrink-0 ${applyTax ? "text-[#18c98f]" : dark ? "text-white/60" : "text-[#2d2c35]/60"}`} />
                  <div className="min-w-0">
                    <div className={`text-xs font-bold truncate ${dark ? "text-white" : "text-[#182138]"}`}>
                      {L.taxLabel} 15%
                    </div>
                    <div className={`text-[9px] font-bold truncate ${applyTax ? "text-[#18c98f]" : dark ? "text-white/50" : "text-[#2d2c35]/50"}`}>
                      {applyTax
                        ? (lang === "ar" ? "مُفعّلة" : "Enabled")
                        : (lang === "ar" ? "اضغط للتفعيل" : "Tap to enable")}
                    </div>
                  </div>
                </div>
                <span
                  aria-hidden
                  className={`shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition ${
                    applyTax ? "bg-[#18c98f]" : dark ? "bg-[#2e3a5c]" : "bg-[#e5e5ea]"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                      applyTax ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </span>
              </button>
              </div>
            </div>

            {/* Explicit "احسب دخلك" CTA — reinforces the compute action and auto
                scrolls back to the LCD so the user sees the new figures. */}
            <button
              data-testid="earnings-calc-btn"
              onClick={scrollToLCD}
              className="mt-3 w-full h-12 rounded-full bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#182138] font-black text-sm inline-flex items-center justify-center gap-2 shadow-[0_12px_28px_-10px_rgba(24,201,143,0.6)] active:scale-[0.98] transition-all"
            >
              <TrendingUp className="h-4 w-4" />
              {lang === "ar" ? "احسب دخلك" : "Calculate my earnings"}
            </button>
          </div>

        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testId, dark, lang, max, icon, compact, onBlur, digits = "en" }) {
  const h = compact ? "h-9" : "h-11";
  const textSize = compact ? "text-sm" : "text-base";
  const labelSize = compact ? "text-[10px]" : "text-sm";
  const mb = compact ? "mb-0.5" : "mb-1.5";
  const displayValue = digits === "ar" ? localizeDigits(String(value), "ar") : value;
  return (
    <div>
      <label className={`block ${labelSize} font-semibold ${mb} ${dark ? "text-white/80" : "text-[#2d2c35]"} ${lang === "ar" ? "font-arabic" : "font-outfit"} truncate`}>
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={displayValue}
          data-testid={testId}
          onFocus={(e) => e.target.select()}
          onBlur={onBlur}
          onChange={(e) => {
            const v = normalizeDigits(e.target.value);
            onChange(v === "" ? 0 : parseFloat(v) || 0);
          }}
          className={`w-full ${h} rounded-lg border px-2 font-mono-num ${textSize} font-bold text-center ${dark ? "bg-[#182138] border-[#2e3a5c] text-white focus:border-[#18c98f]" : "bg-white border-[#e5e5ea]"}`}
          dir="ltr"
        />
        {icon && (
          <span className="absolute inset-y-0 end-2 flex items-center text-[#18c98f]">
            {icon}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultTile({ icon: Icon, label, value, unit, dark, highlighted }) {
  return (
    <div
      className={`rounded-2xl p-3 sm:p-4 ${
        highlighted
          ? "bg-[#18c98f] text-white shadow-[0_10px_30px_-10px_rgba(24,201,143,0.5)]"
          : dark
          ? "bg-[#1f2a47]/60"
          : "bg-white border border-[#e5e5ea]"
      }`}
    >
      <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${highlighted ? "text-[#182138]" : "text-[#18c98f]"}`}>
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className={`mt-1.5 font-bold font-mono-num text-xl sm:text-2xl ${highlighted ? "text-[#182138]" : dark ? "text-white" : "text-[#182138]"}`} dir="ltr">
        {value} <span className="text-xs font-semibold opacity-70">{unit}</span>
      </div>
    </div>
  );
}

/* Compact one-line mini stat — used in the strip under the LCD screen. */
function MiniStat({ label, value, unit, dark }) {
  return (
    <div className="px-2 py-2.5 sm:px-3 sm:py-3 text-center">
      <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-bold ${dark ? "text-[#18c98f]/80" : "text-[#12a777]"} truncate`}>
        {label}
      </div>
      <div className={`mt-1 font-bold font-mono-num text-sm sm:text-base ${dark ? "text-white" : "text-[#182138]"} truncate`} dir="ltr">
        {value}
      </div>
      <div className={`text-[9px] font-semibold opacity-60 ${dark ? "text-white" : "text-[#182138]"}`} dir="ltr">
        {unit}
      </div>
    </div>
  );
}


function ScenarioCard({ title, desc, value, unit, yearly, dark, lang, tier }) {
  const emphasis = tier === "pro";
  return (
    <div
      data-testid={`earnings-scenario-${tier}`}
      className={`rounded-2xl p-4 border transition-all ${
        emphasis
          ? "bg-[#18c98f]/15 border-[#18c98f]"
          : dark
          ? "bg-[#182138]/60 border-[#2e3a5c]"
          : "bg-white border-[#e5e5ea]"
      }`}
    >
      <div className={`text-xs font-bold uppercase tracking-widest ${emphasis ? "text-[#18c98f]" : dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
        {title}
      </div>
      <div className={`text-[11px] mt-0.5 ${dark ? "text-white/50" : "text-[#2d2c35]/60"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
        {desc}
      </div>
      <div className={`mt-3 font-bold font-mono-num text-xl sm:text-2xl text-[#18c98f]`} dir="ltr">
        {value}
      </div>
      <div className={`text-[11px] font-mono-num ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`} dir="ltr">
        {unit}
      </div>
      <div className={`mt-2 text-[10px] font-mono-num ${dark ? "text-white/50" : "text-[#2d2c35]/55"}`} dir="ltr">
        {yearly}
      </div>
    </div>
  );
}
